// src/pages/AuthorsPage.js
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Spinner, Alert, Modal, Badge } from 'react-bootstrap';
import { FaGraduationCap, FaHome, FaSignOutAlt, FaPlus, FaBookmark, 
         FaCheckCircle, FaChalkboardTeacher, FaCompass, FaListUl, FaUser, FaSearch } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { signOut } from "firebase/auth";
import { auth, db, storage } from "../firebase/firebase-config";
import { collection, query, where, getDocs, doc, deleteDoc, getDoc, setDoc, addDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import AuthorCard from '../components/AuthorCard';
import '../styles/Dashboard.css';

function AuthorsPage() {
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('myinstructors');
  
  // Edit/Add Instructor Modal
  const [showInstructorModal, setShowInstructorModal] = useState(false);
  const [editingInstructor, setEditingInstructor] = useState(null);
  const [instructorFormData, setInstructorFormData] = useState({
    name: '',
    imageUrl: '',
    country: '',
    tags: [],
    bio: ''
  });
  const [tagInput, setTagInput] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [instructorToDelete, setInstructorToDelete] = useState(null);
  
  const navigate = useNavigate();
  const isAdmin = localStorage.getItem("isAdmin") === "true";

  // Load instructors from the database on component mount
  useEffect(() => {
    fetchInstructors();
  }, []);

  // Fetch all instructors based on the user's courses
  const fetchInstructors = async () => {
    setLoading(true);
    try {
      const uid = localStorage.getItem("uid");
      if (!uid) {
        throw new Error("User not authenticated");
      }
      
      // First, get all books/courses to extract instructors
      const coursesQuery = query(collection(db, "books"), where("userId", "==", uid));
      const querySnapshot = await getDocs(coursesQuery);
      
      // Extract unique instructors from courses
      const instructorMap = new Map(); // Use a Map to track unique instructors and their course counts
      
      querySnapshot.forEach((doc) => {
        const courseData = doc.data();
        if (courseData.author || courseData.professor) {
          // Use professor field if available, otherwise fall back to author field
          const instructorName = courseData.professor || courseData.author;
          // Split instructors if there are multiple (comma separated)
          const instructors = instructorName.split(',').map(instructor => instructor.trim());
          
          instructors.forEach(instructorName => {
            if (instructorName) {
              // Create or update instructor in the map with course count
              if (instructorMap.has(instructorName)) {
                instructorMap.get(instructorName).bookCount += 1;
              } else {
                instructorMap.set(instructorName, {
                  id: instructorName.toLowerCase().replace(/\s+/g, '-'),
                  name: instructorName,
                  bookCount: 1,
                  tags: courseData.tags || []
                });
              }
            }
          });
        }
      });
      
      // Now, try to fetch additional instructor details if they exist
      const instructorsQuery = query(collection(db, "authors"), where("userId", "==", uid));
      const instructorsSnapshot = await getDocs(instructorsQuery);
      
      // Update the map with instructor details if they exist
      instructorsSnapshot.forEach((doc) => {
        const instructorData = doc.data();
        if (instructorMap.has(instructorData.name)) {
          // Merge the existing course count with the stored instructor data
          const existing = instructorMap.get(instructorData.name);
          instructorMap.set(instructorData.name, {
            ...instructorData,
            bookCount: existing.bookCount,
            id: doc.id // Use the Firestore document ID
          });
        } else {
          // This instructor exists in the authors collection but has no courses
          instructorMap.set(instructorData.name, {
            ...instructorData,
            bookCount: 0,
            id: doc.id
          });
        }
      });
      
      // Convert map to array
      const instructorsList = Array.from(instructorMap.values());
      setInstructors(instructorsList);
      
    } catch (error) {
      console.error("Error fetching instructors:", error);
      setError("Failed to load instructors. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Filter instructors based on search query
  const filteredInstructors = instructors.filter(instructor => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      instructor.name.toLowerCase().includes(query) ||
      (instructor.country && instructor.country.toLowerCase().includes(query)) ||
      (instructor.tags && instructor.tags.some(tag => tag.toLowerCase().includes(query)))
    );
  });

  // Handle form change for instructor editing
  const handleChange = (e) => {
    const { name, value } = e.target;
    setInstructorFormData({
      ...instructorFormData,
      [name]: value
    });
  };

  // Add a tag to the instructor
  const handleAddTag = () => {
    if (tagInput && !instructorFormData.tags.includes(tagInput)) {
      setInstructorFormData({
        ...instructorFormData,
        tags: [...instructorFormData.tags, tagInput]
      });
      setTagInput('');
    }
  };

  // Remove a tag from the instructor
  const handleRemoveTag = (tagToRemove) => {
    setInstructorFormData({
      ...instructorFormData,
      tags: instructorFormData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  // Handle image file selection
  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      
      // Create a preview URL for the image
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload image to Firebase Storage
  const uploadImageToFirebase = async (file) => {
    if (!file) return null;
    
    const uid = localStorage.getItem("uid");
    const storageRef = ref(storage, `instructors/${uid}/${Date.now()}_${file.name}`);
    
    // Upload the file with progress monitoring
    const uploadTask = uploadBytesResumable(storageRef, file);
    
    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          reject(error);
        },
        async () => {
          // Get the download URL
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(downloadURL);
        }
      );
    });
  };

  // Add or update an instructor
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const uid = localStorage.getItem("uid");
      if (!uid) {
        throw new Error("User not authenticated");
      }
      
      // Upload image if selected
      let imageUrl = instructorFormData.imageUrl;
      if (imageFile) {
        imageUrl = await uploadImageToFirebase(imageFile);
      }
      
      const instructorData = {
        ...instructorFormData,
        imageUrl,
        userId: uid,
        updatedAt: new Date().toISOString()
      };
      
      if (editingInstructor) {
        // Update existing instructor
        await setDoc(doc(db, "authors", editingInstructor.id), instructorData, { merge: true });
      } else {
        // Add new instructor
        await addDoc(collection(db, "authors"), {
          ...instructorData,
          createdAt: new Date().toISOString()
        });
      }
      
      // Refresh the instructors list
      await fetchInstructors();
      
      // Close the modal and reset form
      setShowInstructorModal(false);
      resetForm();
      
    } catch (error) {
      console.error("Error saving instructor:", error);
      setError("Failed to save instructor. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset the form fields
  const resetForm = () => {
    setInstructorFormData({
      name: '',
      imageUrl: '',
      country: '',
      tags: [],
      bio: ''
    });
    setTagInput('');
    setImageFile(null);
    setImagePreview(null);
    setUploadProgress(0);
    setEditingInstructor(null);
  };

  // Open the edit modal with instructor data
  const handleEditInstructor = (instructor) => {
    setEditingInstructor(instructor);
    setInstructorFormData({
      name: instructor.name || '',
      imageUrl: instructor.imageUrl || '',
      country: instructor.country || '',
      tags: instructor.tags || [],
      bio: instructor.bio || ''
    });
    setImagePreview(instructor.imageUrl);
    setShowInstructorModal(true);
  };

  // Show delete confirmation modal
  const handleDeleteClick = (instructorId) => {
    setInstructorToDelete(instructorId);
    setShowDeleteModal(true);
  };

  // Delete an instructor
  const confirmDelete = async () => {
    if (!instructorToDelete) return;
    
    try {
      await deleteDoc(doc(db, "authors", instructorToDelete));
      
      // Remove from UI
      setInstructors(instructors.filter(instructor => instructor.id !== instructorToDelete));
      setShowDeleteModal(false);
      setInstructorToDelete(null);
    } catch (error) {
      console.error("Error deleting instructor:", error);
      setError("Failed to delete instructor. Please try again.");
    }
  };

  // Sign out
  const handleSignOut = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem("uid");
      localStorage.removeItem("isAdmin");
      navigate("/login");
    } catch (error) {
      console.error("Error signing out: ", error);
      setError("Failed to sign out. Please try again.");
    }
  };

  return (
    <div className="dashboard-container">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <FaGraduationCap className="me-2" /> Course Manager
          </div>
        </div>

        <div className="sidebar-nav">
          <div className="nav-item">
            <a 
              href="/" 
              className="nav-link"
            >
              <FaHome className="nav-icon" /> Dashboard
            </a>
          </div>

          <div className="sidebar-divider"></div>
          
          <div className="sidebar-heading">Actions</div>
          
          <div className="nav-item">
            <a href="/course/new" className="nav-link">
              <FaPlus className="nav-icon" /> Add New Course
            </a>
          </div>

          <div className="sidebar-divider"></div>
          
          <div className="sidebar-heading">My Courses</div>
          
          <div className="nav-item">
            <a 
              href="/#completed" 
              className="nav-link"
            >
              <FaCheckCircle className="nav-icon" /> Completed
            </a>
          </div>
          
          <div className="nav-item">
            <a 
              href="/#inprogress" 
              className="nav-link"
            >
              <FaChalkboardTeacher className="nav-icon" /> In Progress
            </a>
          </div>
          
          <div className="nav-item">
            <a 
              href="/#upcoming" 
              className="nav-link"
            >
              <FaBookmark className="nav-icon" /> Upcoming
            </a>
          </div>
          
          {/* Instructors - active */}
          <div className="nav-item">
            <a 
              href="#" 
              className="nav-link active"
            >
              <FaUser className="nav-icon" /> Instructors
            </a>
          </div>

          <div className="sidebar-divider"></div>
          
          <div className="nav-item">
            <a href="#" className="nav-link" onClick={handleSignOut}>
              <FaSignOutAlt className="nav-icon" /> Sign Out
            </a>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <Container fluid>
          {/* Header & Search Row */}
          <Row className="mb-4 align-items-center">
            <Col lg={6} className="mb-3 mb-lg-0">
              <h2 className="mb-0">Instructors</h2>
              <p className="text-muted mb-0">
                {filteredInstructors.length > 0 
                  ? `You have ${filteredInstructors.length} ${filteredInstructors.length === 1 ? 'instructor' : 'instructors'} in your collection.` 
                  : 'No instructors in your collection yet.'}
              </p>
            </Col>
            <Col lg={6}>
              <div className="d-flex justify-content-lg-end">
                <div className="search-container w-100" style={{ maxWidth: '400px' }}>
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search instructors..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
            </Col>
          </Row>

          {/* Status Messages */}
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-4">
              {error}
            </Alert>
          )}

          {/* Instructors Grid */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status" variant="primary" />
              <p className="mt-3">Loading instructors...</p>
            </div>
          ) : filteredInstructors.length > 0 ? (
            <Row className="g-4">
              {filteredInstructors.map((instructor) => (
                <Col key={instructor.id} lg={15/5} md={3} sm={6} xs={12}>
                  <AuthorCard 
                    author={instructor} 
                    onEdit={() => handleEditInstructor(instructor)}
                    onDelete={() => handleDeleteClick(instructor.id)}
                  />
                </Col>
              ))}
            </Row>
          ) : (
            <div className="empty-state py-5 my-4 text-center">
              <div className="py-5">
                <h4 className="text-muted mb-3">
                  {searchQuery 
                    ? 'No instructors found matching your search.' 
                    : 'No instructors in your collection yet.'}
                </h4>
                {!searchQuery && (
                  <p>
                    Add instructors manually or they will be automatically extracted from your courses.
                    <div className="mt-3">
                      <Button variant="primary" onClick={() => setShowInstructorModal(true)}>
                        <FaPlus className="me-2" /> Add New Instructor
                      </Button>
                    </div>
                  </p>
                )}
              </div>
            </div>
          )}
        </Container>
      </main>

      {/* Add/Edit Instructor Modal */}
      <Modal show={showInstructorModal} onHide={() => setShowInstructorModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingInstructor ? 'Edit Instructor' : 'Add New Instructor'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Instructor Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={instructorFormData.name}
                    onChange={handleChange}
                    placeholder="Enter instructor name"
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Institution/University</Form.Label>
                  <Form.Control
                    type="text"
                    name="country"
                    value={instructorFormData.country}
                    onChange={handleChange}
                    placeholder="Institution or university"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Instructor Bio</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="bio"
                    value={instructorFormData.bio}
                    onChange={handleChange}
                    placeholder="Brief biography"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Instructor Image</Form.Label>
                  <div className="mb-3">
                    {(imagePreview || instructorFormData.imageUrl) && (
                      <div className="text-center mb-3">
                        <img 
                          src={imagePreview || instructorFormData.imageUrl} 
                          alt="Instructor preview" 
                          style={{ 
                            width: '150px', 
                            height: '150px', 
                            objectFit: 'cover',
                            borderRadius: '50%'
                          }} 
                        />
                      </div>
                    )}
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{ display: 'none' }}
                      id="instructor-image-upload"
                    />
                    <div className="d-flex mb-2 align-items-center">
                      <Button 
                        variant="outline-secondary" 
                        onClick={() => document.getElementById('instructor-image-upload').click()}
                        disabled={isSubmitting}
                      >
                        Choose Image
                      </Button>
                      {(imageFile || instructorFormData.imageUrl) && (
                        <Button
                          variant="link"
                          className="text-danger ms-2"
                          onClick={() => {
                            setImageFile(null);
                            setImagePreview(null);
                            setInstructorFormData({
                              ...instructorFormData,
                              imageUrl: ''
                            });
                          }}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                    <Form.Text className="text-muted">
                      Upload an image of the instructor or leave blank for default.
                    </Form.Text>
                    {uploadProgress > 0 && uploadProgress < 100 && (
                      <div className="mt-2">
                        <div className="progress">
                          <div 
                            className="progress-bar" 
                            role="progressbar" 
                            style={{ width: `${uploadProgress}%` }}
                            aria-valuenow={uploadProgress} 
                            aria-valuemin="0" 
                            aria-valuemax="100"
                          >
                            {Math.round(uploadProgress)}%
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Image URL</Form.Label>
                  <Form.Control
                    type="text"
                    name="imageUrl"
                    value={instructorFormData.imageUrl}
                    onChange={handleChange}
                    placeholder="Enter image URL (optional if uploading file)"
                    disabled={!!imageFile}
                  />
                  <Form.Text className="text-muted">
                    Alternative to uploading: enter direct URL to instructor image
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Subject Areas</Form.Label>
                  <div className="d-flex mb-2">
                    <Form.Control
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      placeholder="Add a subject area"
                    />
                    <Button 
                      variant="outline-secondary" 
                      onClick={handleAddTag} 
                      className="ms-2"
                      disabled={!tagInput}
                    >
                      Add
                    </Button>
                  </div>
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {instructorFormData.tags.map((tag, index) => (
                      <Badge 
                        key={index} 
                        bg="secondary"
                        className="d-flex align-items-center px-3 py-2"
                      >
                        {tag}
                        <span 
                          className="ms-2 cursor-pointer" 
                          onClick={() => handleRemoveTag(tag)}
                          style={{ cursor: 'pointer' }}
                        >
                          ×
                        </span>
                      </Badge>
                    ))}
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end mt-4">
              <Button 
                variant="secondary" 
                onClick={() => setShowInstructorModal(false)}
                className="me-2"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Saving...
                  </>
                ) : (
                  'Save Instructor'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this instructor? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default AuthorsPage;